import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import moment from 'moment'
import { saveAs } from 'file-saver'
import { groupBy, keys, noop } from 'lodash'
import Modal from '@ublique/components/LoadingModal'
import Button from '@ublique/components/Button'

import { selectModels } from '../selectors'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import ErrorsAccordion from 'components/ImportModal/ErrorsAccordion'

const MAX_ROWS = 5

const ImportError = ({ close, open, data }) => {
  const { instance, wt, documentType, errors, tryAgain = noop } = data

  const models = useStoreSelector('appConfig', selectModels)
  const { t } = useTranslation(['Document', 'Shell', 'ImportLog'])
  const { t: modelsT } = useTranslation('Models')

  const translateDocumentTitle = useCallback(
    (documentType) => {
      return modelsT(
        `DocumentModel-${documentType}.${models[documentType]?.title}`
      )
    },
    [modelsT, documentType, models]
  )

  const errorsGroups = useMemo(() => groupBy(errors, 'sheetName'), [errors])

  const downloadLogs = useCallback(() => {
    const reduceDocument = (acc, { fieldName, rowNumber, reason }) =>
      `${acc} ${t('Shell:ImportRow')} ${rowNumber}: ${fieldName} - ${t(
        'ImportLog:' + reason
      )}\n\n`

    // Create a text listed of various products
    let text = `Import log: ${translateDocumentTitle(documentType) + '\n\n'}`
    text =
      text +
      keys(errorsGroups).reduce(
        (acc, document) =>
          `${acc}\n${document}\n${errorsGroups[document].reduce(
            reduceDocument,
            ''
          )}`,
        ''
      )

    const blobText = new Blob([text], { type: 'text/plain' })
    const fileName = `Import_Error_${moment().format('YYYY-MM-DD_HH-mm')}.txt`

    saveAs(blobText, fileName)
  }, [errorsGroups, documentType, translateDocumentTitle])

  const tryAgainFunc = useCallback(() => {
    tryAgain()
    close()
  })

  const groupBySheetName = (data) => Object.entries(groupBy(data, 'sheetName'))

  const errorsBySheetList = groupBySheetName(errors)

  const infoMessage = useMemo(() => {
    return (
      <p className='error-document'>
        Document: <strong>{translateDocumentTitle(documentType)}</strong>
      </p>
    )
  }, [documentType, wt, instance])

  const modalBody = () => {
    return (
      <>
        <h5>{t('ImportErrorMessage')}</h5>
        {documentType && infoMessage}
        <ErrorsAccordion errorsBySheetList={errorsBySheetList} t={t} />
      </>
    )
  }

  return (
    <Modal
      id='modal-import-error'
      preventCloseOnClickOutside
      modalHeading={t('ImportErrorTitle')}
      open={open}
      onRequestClose={close}
      size='md'
      footerCustom={
        <>
          <Button kind='secondary' onClick={close}>
            {t('close')}
          </Button>
          <Button
            disabled={errors.length <= MAX_ROWS}
            kind='primary'
            onClick={downloadLogs}
          >
            {t('DownloadErrorLogs')}
          </Button>
          <Button kind='primary' onClick={tryAgainFunc}>
            {t('TryAgain')}
          </Button>
        </>
      }
    >
      {modalBody()}
    </Modal>
  )
}

export default ImportError
